/* eslint-disable fp/no-mutation */
import React, { useRef, useCallback, useEffect } from 'react'
import { Box, Typography, Slider, Paper } from '@mui/material'
import { CANVAS_WIDTH, CANVAS_HEIGHT } from './constants'

let drawCertificateTemplate = (ctx: CanvasRenderingContext2D) => {
  ctx.fillStyle = 'black'

  // Draw certificate fields (labels on left, protected areas on right)
  ctx.font = 'bold 32px Arial'
  ctx.textAlign = 'right'

  // Field labels
  ctx.fillText('Name', 280, 210)
  ctx.fillText('License #', 280, 270)
  ctx.fillText('License State', 280, 330)
  ctx.fillText('License Type', 280, 390)
  ctx.fillText('Has Completed', 280, 480)
  ctx.fillText('Date', 280, 540)
  ctx.fillText('Hour(s)', 280, 600)

  // Draw vertical lines
  ctx.strokeStyle = 'black'
  ctx.lineWidth = 4

  // First group vertical line (Name through License Type)
  ctx.beginPath()
  ctx.moveTo(300, 175) // Start above Name
  ctx.lineTo(300, 410) // End below License Type
  ctx.stroke()

  // Second group vertical line (Has Completed through Hour(s))
  ctx.beginPath()
  ctx.moveTo(300, 445) // Start above Has Completed
  ctx.lineTo(300, 620) // End below Hour(s)
  ctx.stroke()
}

type BackgroundConfigStepProps = {
  image: File | undefined
  opacity: number
  onOpacityChange: (opacity: number) => void
  onGenerateFlattenedBackground: (dataUrl: string) => void
}

export default ({
  image,
  opacity,
  onOpacityChange,
  onGenerateFlattenedBackground,
}: BackgroundConfigStepProps) => {
  let canvasRef = useRef<HTMLCanvasElement>(null)

  let drawPreview = useCallback(() => {
    let canvas = canvasRef.current
    if (!canvas || !image) return

    let ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas to actual certificate dimensions (11" x 8.5" at 150 DPI)
    canvas.width = CANVAS_WIDTH
    canvas.height = CANVAS_HEIGHT

    // Fill with white background
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    let img = new Image()
    img.onload = () => {
      // Calculate dimensions to fit image within canvas while maintaining aspect ratio
      let imgAspectRatio = img.width / img.height
      let canvasAspectRatio = CANVAS_WIDTH / CANVAS_HEIGHT

      let drawWidth
      let drawHeight
      let drawX
      let drawY

      if (imgAspectRatio > canvasAspectRatio) {
        // Image is wider - fit to canvas width
        drawWidth = CANVAS_WIDTH
        drawHeight = CANVAS_WIDTH / imgAspectRatio
        drawX = 0
        drawY = (CANVAS_HEIGHT - drawHeight) / 2
      } else {
        // Image is taller - fit to canvas height
        drawHeight = CANVAS_HEIGHT
        drawWidth = CANVAS_HEIGHT * imgAspectRatio
        drawX = (CANVAS_WIDTH - drawWidth) / 2
        drawY = 0
      }

      // Draw image with opacity
      ctx.globalAlpha = opacity
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
      ctx.globalAlpha = 1

      // Draw certificate template
      drawCertificateTemplate(ctx)
    }
    img.src = URL.createObjectURL(image)
  }, [image, opacity])

  let generateFlattenedBackground = useCallback(() => {
    if (!image) return

    let canvas = document.createElement('canvas')
    canvas.width = CANVAS_WIDTH
    canvas.height = CANVAS_HEIGHT
    let ctx = canvas.getContext('2d')
    if (!ctx) return

    // Fill with white background
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    let img = new Image()
    img.onload = () => {
      // Calculate dimensions to fit image within canvas while maintaining aspect ratio
      let imgAspectRatio = img.width / img.height
      let canvasAspectRatio = CANVAS_WIDTH / CANVAS_HEIGHT

      let drawWidth
      let drawHeight
      let drawX
      let drawY

      if (imgAspectRatio > canvasAspectRatio) {
        drawWidth = CANVAS_WIDTH
        drawHeight = CANVAS_WIDTH / imgAspectRatio
        drawX = 0
        drawY = (CANVAS_HEIGHT - drawHeight) / 2
      } else {
        drawHeight = CANVAS_HEIGHT
        drawWidth = CANVAS_HEIGHT * imgAspectRatio
        drawX = (CANVAS_WIDTH - drawWidth) / 2
        drawY = 0
      }

      // Draw image with opacity
      ctx.globalAlpha = opacity
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
      ctx.globalAlpha = 1

      // Draw certificate template
      drawCertificateTemplate(ctx)

      // Convert to data URL and pass to parent
      let dataUrl = canvas.toDataURL('image/png')
      onGenerateFlattenedBackground(dataUrl)
    }
    img.src = URL.createObjectURL(image)
  }, [image, opacity, onGenerateFlattenedBackground])

  useEffect(() => {
    drawPreview()
  }, [drawPreview])

  // Generate flattened background when opacity changes
  useEffect(() => {
    generateFlattenedBackground()
  }, [generateFlattenedBackground])

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Configure background opacity
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Background Opacity: {Math.round(opacity * 100)}%
        </Typography>

        <Slider
          value={opacity}
          onChange={(_, value) => onOpacityChange(value as number)}
          min={0.05}
          max={0.5}
          step={0.05}
          sx={{ width: '100%', margin: '0 30px' }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Lower opacity works better for certificate backgrounds
        </Typography>
      </Box>

      <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Preview (11" × 8.5" landscape)
        </Typography>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 1,
          }}
        >
          <Box
            component="canvas"
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            sx={{
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </Box>
      </Paper>
    </Box>
  )
}
